import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  useEffect,
  useState,
} from "react";
import { cn } from "../lib/utils";
import { LongOuCourt } from "./cadres-ui";
import type { ApercuReponse } from "./contract";

/**
 * L'apercu en perspective du module Cadres (#167, tache B5 ; spec, section 4). Les images
 * detourees des plaques, empilees dans un conteneur en `perspective`, chacune a sa profondeur
 * (`translateZ`), l'anneau du cadre par-dessus, une ombre portee par plaque (`drop-shadow`,
 * qui suit la transparence comme l'ombre reelle). Aucune bibliotheque 3D : du CSS.
 *
 * La souris, le doigt (un glisser horizontal : le defilement vertical de la page reste libre)
 * ou les fleches du clavier inclinent l'ensemble jusqu'a 25 degres. La profondeur est la vraie :
 * la plaque de rang r recule de (r - 1) pas, le pas du cadre (`dimensions.pas_mm`, l'epaisseur
 * d'une plaque plus l'ecart), et l'ecart separe assez les plaques pour qu'on n'exagere plus
 * rien. Avec `prefers-reduced-motion`, l'apercu reste fixe et legerement incline.
 *
 * L'anneau n'est plus centre en hauteur : la bande du couvercle, en haut, et celle du bas du
 * cadre different. La page deduit le haut du hors tout (hauteur hors tout, moins la hauteur
 * d'une plaque, moins la bande du bas, qui vaut un flanc). Avec une LED (`rapport.led`), la plaque du
 * fond est eclairee ; c'est une idee de l'effet, que la page dit en petit.
 *
 * Les millimetres deviennent des pixels par une unite de conteneur : `--mm` vaut la largeur
 * de la scene divisee par la largeur hors tout du cadre. Rien n'est mesure en JavaScript.
 */

export const INCLINAISON_MAX_DEG = 25;

export type Pente = { rx: number; ry: number };

/** La pente de repos, qui montre la profondeur sans rien toucher ; celle, fixe, du mouvement reduit. */
export const PENTE_DE_REPOS: Pente = { rx: 5, ry: -8 };

const PAS_CLAVIER_DEG = 5;

/** De combien le devant du cadre depasse la plaque 1 (sa levre et son jeu). */
const AVANT_DU_CADRE_MM = 2;

/** La part de la largeur de la scene prise par le cadre : le reste laisse tourner les coins. */
const PART_DU_CADRE = 0.76;

/** Le fond d'un cadre a LED : plus clair, avec un halo, comme si le ruban l'eclairait de derriere. */
const FOND_ECLAIRE = "brightness(1.35) drop-shadow(0 0 12px rgba(255, 236, 190, 0.9))";

const IDEE_DE_L_EFFET = "Le fond éclairé est une idée de l'effet, pas un rendu.";
const CONSEIL_CLAVIER = "Flèches du clavier pour incliner.";

/** La plaque isolee reste pleine, les autres s'estompent (maquette `CadresPlaques.dc.html`). */
const OPACITE_ESTOMPEE = 0.14;
const OPACITE_CADRE_ESTOMPE = 0.3;

type Boite = { left: number; top: number; width: number; height: number };

function borner(degres: number): number {
  return Math.max(-INCLINAISON_MAX_DEG, Math.min(INCLINAISON_MAX_DEG, degres));
}

/** Au dixieme de degre, et jamais -0 (qui s'ecrirait pareil mais ne se compare pas pareil). */
function propre(degres: number): number {
  const arrondi = Math.round(degres * 10) / 10;
  return arrondi === 0 ? 0 : arrondi;
}

/**
 * La pente sous le pointeur : nulle au centre de la scene, 25 degres aux bords, bornee au-dela.
 * Le pointeur a droite fait venir le bord droit vers soi (on regarde le cadre depuis la
 * droite), le pointeur en haut fait venir le haut.
 */
export function inclinaison(x: number, y: number, boite: Boite): Pente {
  if (boite.width <= 0 || boite.height <= 0) return { rx: 0, ry: 0 };
  const nx = ((x - boite.left) / boite.width) * 2 - 1;
  const ny = ((y - boite.top) / boite.height) * 2 - 1;
  return {
    rx: propre(borner(ny * INCLINAISON_MAX_DEG)),
    ry: propre(borner(-nx * INCLINAISON_MAX_DEG)),
  };
}

/** Les fleches du clavier : ce qu'elles ajoutent a la pente. */
const FLECHES: Record<string, Pente> = {
  ArrowLeft: { rx: 0, ry: PAS_CLAVIER_DEG },
  ArrowRight: { rx: 0, ry: -PAS_CLAVIER_DEG },
  ArrowUp: { rx: -PAS_CLAVIER_DEG, ry: 0 },
  ArrowDown: { rx: PAS_CLAVIER_DEG, ry: 0 },
};

const REQUETE_MOUVEMENT_REDUIT = "(prefers-reduced-motion: reduce)";

function lireMouvementReduit(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia(REQUETE_MOUVEMENT_REDUIT).matches
    : false;
}

/** Vrai quand le systeme demande moins de mouvement, suivi s'il change en cours de route. */
export function useMouvementReduit(): boolean {
  const [reduit, setReduit] = useState(lireMouvementReduit);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const requete = window.matchMedia(REQUETE_MOUVEMENT_REDUIT);
    const suivre = (): void => setReduit(requete.matches);
    requete.addEventListener?.("change", suivre);
    return () => requete.removeEventListener?.("change", suivre);
  }, []);
  return reduit;
}

export function Perspective({ apercu, isolee }: { apercu: ApercuReponse; isolee: number | null }) {
  const reduit = useMouvementReduit();
  const [pente, setPente] = useState<Pente>(PENTE_DE_REPOS);
  const vue = reduit ? PENTE_DE_REPOS : pente;

  const [largeurHorsTout, hauteurHorsTout] = apercu.dimensions.hors_tout_mm;
  const [largeurPlaque, hauteurPlaque] = apercu.dimensions.plaques_mm;
  const pasPlaques = apercu.dimensions.pas_mm;
  const largeur = (largeurPlaque / largeurHorsTout) * 100;
  const hauteur = (hauteurPlaque / hauteurHorsTout) * 100;
  // Les plaques sont centrees d'un flanc a l'autre ; le bas du cadre ajoute autant qu'un flanc,
  // et tout le reste de la hauteur est au-dessus (le couvercle).
  const flanc = (largeurHorsTout - largeurPlaque) / 2;
  const haut = ((hauteurHorsTout - hauteurPlaque - flanc) / hauteurHorsTout) * 100;
  const allumee =
    apercu.rapport.led === true ? Math.max(...apercu.plaques.map((p) => p.rang)) : null;
  // Du fond vers l'avant : l'ordre de peinture suit la profondeur, meme sans tri 3D.
  const duFond = [...apercu.plaques].sort((a, b) => b.rang - a.rang);
  // Au millieme : -(4 - 1) * 5,6 vaut -16,799999999999997 en JavaScript, et cela s'ecrirait tel quel.
  const profondeur = (mm: number): string =>
    `translateZ(calc(var(--mm) * ${Math.round(mm * 1000) / 1000}))`;
  const idee = apercu.rapport.led === true ? IDEE_DE_L_EFFET : "";
  const legende = reduit ? idee : `${idee} ${CONSEIL_CLAVIER}`.trim();

  const clavier = (event: KeyboardEvent<HTMLDivElement>): void => {
    const pas = FLECHES[event.key];
    if (!pas) return;
    event.preventDefault();
    setPente((actuelle) => ({
      rx: propre(borner(actuelle.rx + pas.rx)),
      ry: propre(borner(actuelle.ry + pas.ry)),
    }));
  };

  const pointeur = (event: PointerEvent<HTMLDivElement>): void => {
    setPente(
      inclinaison(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect()),
    );
  };

  return (
    <div>
      {/* Focalisable : l'apercu s'incline aux fleches. Pour un lecteur d'ecran, il reste une
          image, decrite par son nom ; les plaques qu'il empile sont decoratives. */}
      <div
        aria-label={
          reduit ? "Le cadre en perspective" : "Le cadre en perspective ; flèches pour incliner"
        }
        className={cn(
          "overflow-hidden rounded-lg border border-border bg-muted/40 py-[7%]",
          !reduit &&
            "touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
        )}
        onKeyDown={reduit ? undefined : clavier}
        onPointerLeave={reduit ? undefined : () => setPente(PENTE_DE_REPOS)}
        onPointerMove={reduit ? undefined : pointeur}
        role="img"
        tabIndex={reduit ? undefined : 0}
      >
        {/* La perspective sur le parent DIRECT du plateau : elle ne vaut que pour les enfants
            directs de l'element qui la porte. Posee sur la scene, deux niveaux plus haut, elle
            ne faisait rien, et le cadre penche se cisaillait en parallelogramme au lieu du
            trapeze de la maquette (relecture de #176, mesure dans Chromium). */}
        <div
          className="relative mx-auto [container-type:inline-size] [perspective:1200px]"
          style={{
            width: `${PART_DU_CADRE * 100}%`,
            aspectRatio: `${largeurHorsTout} / ${hauteurHorsTout}`,
          }}
        >
          <div
            className="absolute inset-0 transition-transform duration-150 ease-out [transform-style:preserve-3d] motion-reduce:transition-none"
            data-plateau
            style={
              {
                transform: `rotateX(${vue.rx}deg) rotateY(${vue.ry}deg)`,
                "--mm": `calc(100cqw / ${largeurHorsTout})`,
              } as CSSProperties
            }
          >
            {duFond.map((plaque) => (
              <img
                alt=""
                className="absolute [filter:drop-shadow(0_1px_2px_rgba(0,0,0,0.35))] transition-opacity duration-150"
                data-rang={plaque.rang}
                draggable={false}
                key={plaque.rang}
                src={`data:image/png;base64,${plaque.png}`}
                style={{
                  left: `${(100 - largeur) / 2}%`,
                  top: `${haut}%`,
                  width: `${largeur}%`,
                  height: `${hauteur}%`,
                  transform: profondeur(-(plaque.rang - 1) * pasPlaques),
                  opacity: isolee === null || isolee === plaque.rang ? 1 : OPACITE_ESTOMPEE,
                  ...(plaque.rang === allumee ? { filter: FOND_ECLAIRE } : {}),
                }}
              />
            ))}
            <img
              alt=""
              className="absolute inset-0 size-full [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.3))] transition-opacity duration-150"
              data-cadre
              draggable={false}
              src={`data:image/png;base64,${apercu.cadre_png}`}
              style={{
                transform: profondeur(AVANT_DU_CADRE_MM),
                opacity: isolee === null ? 1 : OPACITE_CADRE_ESTOMPE,
              }}
            />
          </div>
        </div>
      </div>
      {legende ? (
        // Sans LED, le telephone n'a rien a dire (pas de clavier) : le paragraphe s'efface.
        <p className={cn("mt-2 text-[13px] text-muted-foreground", !idee && "hidden md:block")}>
          <LongOuCourt court={idee} long={legende} />
        </p>
      ) : null}
    </div>
  );
}
